import { Feature, Geometry, GeoJsonProperties } from 'geojson';
import { BaseMapController } from '../core/BaseMapController';
import { IPolygonConfig } from '../types';

/**
 * PolygonManager - handles all polygon-related functionality
 * - Adding/removing/updating polygons
 * - Polygon rendering and styling
 * - State management
 */
export class PolygonManager {
  private mapController: BaseMapController;
  private polygons: IPolygonConfig[] = [];

  constructor(mapController: BaseMapController) {
    this.mapController = mapController;
  }

  addPolygon(polygon: IPolygonConfig): void {
    try {
      const existingPolygon = this.polygons.find(p => p.id === polygon.id);
      if (existingPolygon) {
        this.updatePolygon(polygon);
        return;
      }

      this.polygons.push(polygon);

      const polygonData: Feature<Geometry, GeoJsonProperties> = {
        type: 'Feature',
        properties: { id: polygon.id },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              ...polygon.coordinates.map(coord => [coord.lng, coord.lat]),
              [polygon.coordinates[0].lng, polygon.coordinates[0].lat],
            ],
          ],
        },
      };

      if (this.mapController.getMap().getSource(`polygon-${polygon.id}`)) {
        console.warn('Polygon source already exists, removing first:', polygon.id);
        this.removePolygon(polygon.id);
        this.polygons.push(polygon);
      }

      this.mapController.getMap().addSource(`polygon-${polygon.id}`, {
        type: 'geojson',
        data: polygonData,
      });

      // Add fill layer
      this.mapController.getMap().addLayer({
        id: `polygon-fill-${polygon.id}`,
        type: 'fill',
        source: `polygon-${polygon.id}`,
        layout: {},
        paint: {
          'fill-color': polygon.fillColor || '#4ecdc4',
          'fill-opacity': polygon.fillOpacity || 0.3,
        },
      });

      // Add stroke layer
      this.mapController.getMap().addLayer({
        id: `polygon-stroke-${polygon.id}`,
        type: 'line',
        source: `polygon-${polygon.id}`,
        layout: {},
        paint: {
          'line-color': polygon.strokeColor || '#4ecdc4',
          'line-width': polygon.strokeWidth || 2,
          'line-opacity': polygon.strokeOpacity || 1,
        },
      });
    } catch (error) {
      console.error('Error adding polygon:', error);
    }
  }

  updatePolygon(polygon: IPolygonConfig): void {
    try {
      const existingPolygonIndex = this.polygons.findIndex(p => p.id === polygon.id);

      if (existingPolygonIndex === -1) {
        this.addPolygon(polygon);
        return;
      }

      this.polygons[existingPolygonIndex] = polygon;

      const polygonData: Feature<Geometry, GeoJsonProperties> = {
        type: 'Feature',
        properties: { id: polygon.id },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              ...polygon.coordinates.map(coord => [coord.lng, coord.lat]),
              [polygon.coordinates[0].lng, polygon.coordinates[0].lat],
            ],
          ],
        },
      };

      const source = this.mapController
        .getMap()
        .getSource(`polygon-${polygon.id}`) as mapboxgl.GeoJSONSource;

      if (source) {
        source.setData(polygonData);
      } else {
        console.warn('Polygon source not found, falling back to add:', polygon.id);
        this.addPolygon(polygon);
      }
    } catch (error) {
      console.error('Error updating polygon:', error);
      this.addPolygon(polygon);
    }
  }

  removePolygon(polygonId: string): void {
    try {
      this.polygons = this.polygons.filter(p => p.id !== polygonId);

      const map = this.mapController.getMap();

      if (map.getLayer(`polygon-fill-${polygonId}`)) {
        map.removeLayer(`polygon-fill-${polygonId}`);
      }
      if (map.getLayer(`polygon-stroke-${polygonId}`)) {
        map.removeLayer(`polygon-stroke-${polygonId}`);
      }
      if (map.getSource(`polygon-${polygonId}`)) {
        map.removeSource(`polygon-${polygonId}`);
      }
    } catch (error) {
      console.error('Error removing polygon:', error);
    }
  }

  getPolygons(): IPolygonConfig[] {
    return [...this.polygons];
  }

  hasPolygon(id: string): boolean {
    return this.polygons.some(polygon => polygon.id === id);
  }

  getPolygon(polygonId: string): IPolygonConfig | undefined {
    return this.polygons.find(p => p.id === polygonId);
  }

  clearPolygons(): void {
    try {
      this.polygons.forEach(polygon => this.removePolygon(polygon.id));
      this.polygons = [];
    } catch (error) {
      console.error('Error clearing polygons:', error);
    }
  }

  updatePolygonStyle(
    polygonId: string,
    style: Partial<
      Pick<
        IPolygonConfig,
        'fillColor' | 'strokeColor' | 'fillOpacity' | 'strokeOpacity' | 'strokeWidth'
      >
    >
  ): void {
    try {
      const polygon = this.polygons.find(p => p.id === polygonId);
      if (!polygon) {
        console.warn(`Polygon with id "${polygonId}" not found`);
        return;
      }

      if (style.fillColor !== undefined) polygon.fillColor = style.fillColor;
      if (style.strokeColor !== undefined) polygon.strokeColor = style.strokeColor;
      if (style.fillOpacity !== undefined) polygon.fillOpacity = style.fillOpacity;
      if (style.strokeOpacity !== undefined) polygon.strokeOpacity = style.strokeOpacity;
      if (style.strokeWidth !== undefined) polygon.strokeWidth = style.strokeWidth;

      const map = this.mapController.getMap();
      const fillLayerId = `polygon-fill-${polygonId}`;
      const strokeLayerId = `polygon-stroke-${polygonId}`;

      if (map.getLayer(fillLayerId)) {
        if (style.fillColor !== undefined) {
          map.setPaintProperty(fillLayerId, 'fill-color', style.fillColor);
        }
        if (style.fillOpacity !== undefined) {
          map.setPaintProperty(fillLayerId, 'fill-opacity', style.fillOpacity);
        }
      }

      if (map.getLayer(strokeLayerId)) {
        if (style.strokeColor !== undefined) {
          map.setPaintProperty(strokeLayerId, 'line-color', style.strokeColor);
        }
        if (style.strokeOpacity !== undefined) {
          map.setPaintProperty(strokeLayerId, 'line-opacity', style.strokeOpacity);
        }
        if (style.strokeWidth !== undefined) {
          map.setPaintProperty(strokeLayerId, 'line-width', style.strokeWidth);
        }
      }
    } catch (error) {
      console.error('Error updating polygon style:', error);
    }
  }

  getStats(): {
    total: number;
    totalArea: number;
    averageArea: number;
  } {
    const totalArea = this.polygons.reduce((sum, polygon) => {
      return sum + this.calculatePolygonArea(polygon);
    }, 0);

    return {
      total: this.polygons.length,
      totalArea,
      averageArea: this.polygons.length > 0 ? totalArea / this.polygons.length : 0,
    };
  }

  private calculatePolygonArea(polygon: IPolygonConfig): number {
    if (polygon.coordinates.length < 3) return 0;

    let area = 0;
    const coords = polygon.coordinates;

    for (let i = 0; i < coords.length; i++) {
      const j = (i + 1) % coords.length;
      area += coords[i].lng * coords[j].lat;
      area -= coords[j].lng * coords[i].lat;
    }

    return Math.abs(area) / 2;
  }

  getPolygonsByFillColor(color: string): IPolygonConfig[] {
    return this.polygons.filter(polygon => polygon.fillColor === color);
  }

  /**
   * Get polygons within a bounding box
   */
  getPolygonsInBounds(bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }): IPolygonConfig[] {
    return this.polygons.filter(polygon => {
      return polygon.coordinates.some(coord => {
        return (
          coord.lat <= bounds.north &&
          coord.lat >= bounds.south &&
          coord.lng <= bounds.east &&
          coord.lng >= bounds.west
        );
      });
    });
  }

  /**
   * Check if a point is inside a polygon
   */
  isPointInPolygon(point: { lat: number; lng: number }, polygonId: string): boolean {
    const polygon = this.getPolygon(polygonId);
    if (!polygon) return false;

    const coords = polygon.coordinates;
    let inside = false;

    for (let i = 0, j = coords.length - 1; i < coords.length; j = i++) {
      if (
        coords[i].lat > point.lat !== coords[j].lat > point.lat &&
        point.lng <
          ((coords[j].lng - coords[i].lng) * (point.lat - coords[i].lat)) /
            (coords[j].lat - coords[i].lat) +
            coords[i].lng
      ) {
        inside = !inside;
      }
    }

    return inside;
  }
}
